import React, { useState, useRef } from 'react'
import { Editor } from '@tinymce/tinymce-react'
import { Save, Download, Eye, X, CheckCircle, Printer } from 'lucide-react'

interface Contrato {
  id: string
  numero_contrato: string
  conteudo_editado: string
  observacoes?: string
  status: string
}

interface Cliente {
  id: string
  nome_completo: string
  celular: string
  email: string
  telefone?: string
  nome_empresa?: string
  razao_social?: string
  cnpj?: string
  inscricao_estadual?: string
  inscricao_municipal?: string
  endereco?: string
  cep?: string
  bairro?: string
  cidade?: string
  uf?: string
  site?: string
  instagram?: string
  facebook?: string
  whatsapp_anuncios?: string
  responsavel_financeiro?: string
  email_financeiro?: string
  telefone_financeiro?: string
  data_inauguracao?: string
  capital_social?: string
  quantos_colaboradores?: string
  faturamento_empresa?: string
  faturamento_desejado?: string
  cidades_anunciar?: string
  bairros_anunciar?: string
  publico_ideal?: string
  observacoes?: string
  dia_nascimento?: string
  mes_nascimento?: string
  ano_nascimento?: string
  telefone_secundario?: string
}

interface ContratoEditorProps {
  contrato: Contrato
  cliente?: Cliente
  onSave: (conteudo: string, observacoes?: string) => void
  onClose: () => void
  onGeneratePDF?: () => void
  onActivate?: () => void
}

const ContratoEditor: React.FC<ContratoEditorProps> = ({ 
  contrato, 
  cliente,
  onSave, 
  onClose, 
  onGeneratePDF,
  onActivate
}) => {
  const [conteudo, setConteudo] = useState(contrato.conteudo_editado)
  const [observacoes, setObservacoes] = useState(contrato.observacoes || '')
  const [showPreview, setShowPreview] = useState(false)
  const [saving, setSaving] = useState(false)
  const editorRef = useRef<any>(null)

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(conteudo, observacoes)
    } finally {
      setSaving(false)
    }
  }

  const handleEditorChange = (content: string) => {
    setConteudo(content)
  }

  const handlePrint = () => {
    // Substituir variáveis no conteúdo
    const contentWithVariables = replaceContractVariables(conteudo)
    
    // Criar janela de impressão
    const printWindow = window.open('', '_blank', 'width=800,height=600')
    
    if (!printWindow) {
      alert('Por favor, permita pop-ups para imprimir o contrato')
      return
    }
    
    // HTML otimizado para impressão
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <title>Contrato ${contrato.numero_contrato}</title>
        <style>
          @page {
            size: A4;
            margin: 20mm;
          }
          
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
          
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 1.4;
            color: #000;
            text-align: justify;
            padding: 20px;
          }
          
          h1, h2, h3 {
            page-break-after: avoid;
          }
          
          table {
            page-break-inside: avoid;
          }
          
          img {
            max-width: 100%;
            height: auto;
          }
          
          .header {
            text-align: center;
            font-weight: bold;
            margin-bottom: 30px;
          }
          
          .clause {
            margin-bottom: 20px;
          }
          
          .signature-section {
            margin-top: 50px;
            page-break-inside: avoid;
          }
        </style>
      </head>
      <body>
        ${contentWithVariables}
        
        <!-- Botões para controle -->
        <div class="no-print" style="text-align: center; margin-top: 30px; padding: 20px; border-top: 2px solid #ccc;">
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 14px; cursor: pointer; background: #3b82f6; color: white; border: none; border-radius: 5px; margin-right: 10px;">
            🖨️ Imprimir
          </button>
          <button onclick="window.close()" style="padding: 10px 20px; font-size: 14px; cursor: pointer; background: #6b7280; color: white; border: none; border-radius: 5px;">
            ❌ Fechar
          </button>
        </div>
      </body>
      </html>
    `)
    
    printWindow.document.close()
    
    // Auto-imprimir após carregar
    printWindow.onload = () => {
      printWindow.print()
    }
  }

  // Função para substituir todas as variáveis do sistema no contrato
  const replaceContractVariables = (content: string): string => {
    if (!cliente) {
      console.log('🚫 No client data available for variable replacement')
      return content
    }
    
    console.log('🔄 Replacing variables for client:', cliente.nome_completo)

    // Criar data atual formatada
    const hoje = new Date()
    const dataAtual = hoje.toLocaleDateString('pt-BR')
    const mesAtual = hoje.toLocaleDateString('pt-BR', { month: 'long' })
    const anoAtual = hoje.getFullYear().toString()
    
    // Criar data de nascimento completa se disponível
    let dataNascimento = ''
    if (cliente.dia_nascimento && cliente.mes_nascimento && cliente.ano_nascimento) {
      dataNascimento = `${cliente.dia_nascimento}/${cliente.mes_nascimento}/${cliente.ano_nascimento}`
    }

    // Mapeamento completo de todas as variáveis do sistema
    // Baseado no template real que usa variáveis como CLIENTE_NOME_COMPLETO, CLIENTE_ENDERECO, etc.
    const variables: { [key: string]: string } = {
      // Variáveis principais do template (baseadas no contrato real)
      '{{CLIENTE_NOME_COMPLETO}}': cliente.nome_completo || '',
      '{{CLIENTE_RAZAO_SOCIAL}}': cliente.razao_social || cliente.nome_empresa || '',
      '{{CLIENTE_ENDERECO}}': cliente.endereco || '',
      '{{CLIENTE_BAIRRO}}': cliente.bairro || '',
      '{{CLIENTE_CIDADE}}': cliente.cidade || '',
      '{{CLIENTE_UF}}': cliente.uf || '',
      '{{CLIENTE_CEP}}': cliente.cep || '',
      '{{CLIENTE_CNPJ}}': cliente.cnpj || '',
      '{{CLIENTE_CPF}}': cliente.cpf || '',
      
      // Variáveis alternativas e adicionais
      '{{CLIENTE_NOME}}': cliente.nome_completo || '',
      '{{CLIENTE_EMAIL}}': cliente.email || '',
      '{{CLIENTE_CELULAR}}': cliente.celular || '',
      '{{CLIENTE_TELEFONE}}': cliente.telefone || '',
      '{{CLIENTE_TELEFONE_SECUNDARIO}}': cliente.telefone_secundario || '',
      
      // Dados da empresa
      '{{CLIENTE_NOME_EMPRESA}}': cliente.nome_empresa || '',
      '{{CLIENTE_INSCRICAO_ESTADUAL}}': cliente.inscricao_estadual || '',
      '{{CLIENTE_INSCRICAO_MUNICIPAL}}': cliente.inscricao_municipal || '',
      '{{CLIENTE_DATA_INAUGURACAO}}': cliente.data_inauguracao || '',
      '{{CLIENTE_CAPITAL_SOCIAL}}': cliente.capital_social || '',
      '{{CLIENTE_QUANTOS_COLABORADORES}}': cliente.quantos_colaboradores || '',
      '{{CLIENTE_FATURAMENTO_EMPRESA}}': cliente.faturamento_empresa || '',
      '{{CLIENTE_FATURAMENTO_DESEJADO}}': cliente.faturamento_desejado || '',
      
      // Marketing digital
      '{{CLIENTE_SITE}}': cliente.site || '',
      '{{CLIENTE_INSTAGRAM}}': cliente.instagram || '',
      '{{CLIENTE_FACEBOOK}}': cliente.facebook || '',
      '{{CLIENTE_WHATSAPP_ANUNCIOS}}': cliente.whatsapp_anuncios || '',
      '{{CLIENTE_CIDADES_ANUNCIAR}}': cliente.cidades_anunciar || '',
      '{{CLIENTE_BAIRROS_ANUNCIAR}}': cliente.bairros_anunciar || '',
      '{{CLIENTE_PUBLICO_IDEAL}}': cliente.publico_ideal || '',
      
      // Responsável financeiro
      '{{CLIENTE_RESPONSAVEL_FINANCEIRO}}': cliente.responsavel_financeiro || '',
      '{{CLIENTE_EMAIL_FINANCEIRO}}': cliente.email_financeiro || '',
      '{{CLIENTE_TELEFONE_FINANCEIRO}}': cliente.telefone_financeiro || '',
      
      // Data de nascimento
      '{{CLIENTE_DIA_NASCIMENTO}}': cliente.dia_nascimento || '',
      '{{CLIENTE_MES_NASCIMENTO}}': cliente.mes_nascimento || '',
      '{{CLIENTE_ANO_NASCIMENTO}}': cliente.ano_nascimento || '',
      '{{CLIENTE_DATA_NASCIMENTO}}': dataNascimento,
      
      // Observações
      '{{CLIENTE_OBSERVACOES}}': cliente.observacoes || '',
      
      // Variáveis de sistema/data
      '{{DATA_ATUAL}}': dataAtual,
      '{{MES_ATUAL}}': mesAtual,
      '{{ANO_ATUAL}}': anoAtual,
      
      // Variáveis do contrato
      '{{CONTRATO_NUMERO}}': contrato.numero_contrato || '',
      '{{CONTRATO_STATUS}}': contrato.status || '',
      '{{CONTRATO_OBSERVACOES}}': contrato.observacoes || '',
      
      // Empresa M4 Digital (dados fixos)
      '{{EMPRESA_NOME}}': 'M4 Digital',
      '{{EMPRESA_RAZAO_SOCIAL}}': 'M4 Soluções e Marketing Vendas e Consultorias LTDA',
      '{{EMPRESA_CNPJ}}': '52.277.940/0001-35',
      '{{EMPRESA_ENDERECO}}': 'Rua Inspiração Pedro II, 352 Bairro Nova Petrópolis',
      '{{EMPRESA_CIDADE}}': 'São Bernardo do Campo',
      '{{EMPRESA_UF}}': 'SP',
      '{{EMPRESA_CEP}}': '09760-280'
    }

    // Substituir todas as variáveis no conteúdo
    let replacedContent = content
    console.log('📝 Original content preview:', content.substring(0, 200))
    
    Object.entries(variables).forEach(([variable, value]) => {
      const regex = new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g')
      const matches = content.match(regex)
      if (matches) {
        console.log(`🔄 Replacing ${variable} with "${value}" (found ${matches.length} occurrences)`)
        replacedContent = replacedContent.replace(regex, value)
      }
    })
    
    console.log('✅ Replaced content preview:', replacedContent.substring(0, 200))
    return replacedContent
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-7xl shadow-lg rounded-md bg-white min-h-[95vh]">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              Editor de Contrato: {contrato.numero_contrato}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Edite o conteúdo do contrato e salve as alterações
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Eye className="w-4 h-4 mr-2" />
              {showPreview ? 'Editor' : 'Preview'}
            </button>
            
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Imprimir contrato"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </button>
            
            {onGeneratePDF && (
              <button
                onClick={onGeneratePDF}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <Download className="w-4 h-4 mr-2" />
                Gerar PDF
              </button>
            )}
            
            {contrato.status === 'rascunho' && onActivate && (
              <button
                onClick={onActivate}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Ativar Contrato
              </button>
            )}
            
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex h-full space-x-6">
          {/* Editor Principal */}
          <div className="flex-1">
            {!showPreview ? (
              <div className="h-96">
                <Editor
                  apiKey="g2byz9d0oft9qwfuqs19csc49weref28tgvev6ivsz5iptmw"
                  onInit={(_evt, editor) => editorRef.current = editor}
                  initialValue={conteudo}
                  onEditorChange={handleEditorChange}
                  init={{
                    height: 500,
                    menubar: false,
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'insertdatetime', 'media', 'table', 'help', 'wordcount'
                    ],
                    toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | image link | removeformat | help',
                    // Configuração de upload de imagens
                    images_upload_handler: async (blobInfo, progress) => {
                      return new Promise((resolve, reject) => {
                        const formData = new FormData()
                        formData.append('image', blobInfo.blob(), blobInfo.filename())
                        formData.append('contrato_id', contrato.id)

                        fetch('/api/gestao/contrato-imagens', {
                          method: 'POST',
                          body: formData
                        })
                        .then(response => response.json())
                        .then(result => {
                          if (result.success) {
                            resolve(result.url)
                          } else {
                            reject(result.error || 'Erro no upload')
                          }
                        })
                        .catch(error => {
                          reject('Erro de conexão: ' + error.message)
                        })
                      })
                    },
                    images_upload_base_path: '',
                    images_upload_credentials: true,
                    automatic_uploads: true,
                    paste_data_images: true,
                    branding: false,
                    promotion: false,
                    statusbar: false,
                    elementpath: false,
                    readonly: false,
                    disabled: false,
                    content_style: `
                      body { 
                        font-family: Arial, sans-serif; 
                        font-size: 12px; 
                        line-height: 1.4; 
                        margin: 40px;
                        text-align: justify;
                      }
                      .header { 
                        text-align: center; 
                        font-weight: bold; 
                        margin-bottom: 30px; 
                      }
                      .title { 
                        font-size: 14px; 
                        text-decoration: underline; 
                        margin-bottom: 20px; 
                      }
                      .clause { 
                        margin-bottom: 20px; 
                      }
                      .clause-title { 
                        font-weight: bold; 
                        margin-bottom: 10px; 
                      }
                      img { 
                        max-width: 100%; 
                        height: auto; 
                        margin: 10px 0; 
                      }
                    `,
                    language: 'pt_BR',
                    resize: false
                  }}
                />
              </div>
            ) : (
              <div className="border rounded-lg p-6 bg-white h-96 overflow-y-auto">
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: replaceContractVariables(conteudo) }}
                />
              </div>
            )}
          </div>

          {/* Sidebar com Observações */}
          <div className="w-80 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações
              </label>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Adicione observações sobre este contrato..."
              />
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Informações do Contrato</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Número:</span> {contrato.numero_contrato}
                </div>
                <div>
                  <span className="font-medium">Status:</span> 
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                    contrato.status === 'ativo' ? 'bg-green-100 text-green-800' :
                    contrato.status === 'rascunho' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {contrato.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">💡 Dicas de Edição</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Use <strong>Ctrl+Z</strong> para desfazer</li>
                <li>• Clique em "Preview" para ver o resultado</li>
                <li>• Salve frequentemente suas alterações</li>
                <li>• Use a formatação para destacar informações importantes</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContratoEditor
